import { ThemeOptions } from '@mui/material';

import { ButtonSize } from './Button';
import { tokens } from '../../theme/generated/tokens';
import { DsShadow } from '../../theme/shadows';
import { boxShadowBorder, focusProps } from '../../theme/utils';
import { cssVars } from '../../utils/cssVars';

export const buttonVars = cssVars(
  {
    color: undefined,
    colorHover: undefined,
    colorActive: undefined,
    colorDisabled: undefined,

    background: undefined,
    backgroundHover: undefined,
    backgroundActive: undefined,
    backgroundDisabled: undefined,

    boxShadowHover: undefined,
    boxShadowActive: undefined,

    iconSize: undefined,
    iconGap: undefined,
  },
  'ds-button'
);

export const iconButtonVars = cssVars(
  {
    color: undefined,
    colorActive: undefined,
    colorHover: undefined,
    colorDisabled: undefined,

    background: undefined,
    backgroundHover: undefined,
    backgroundActive: undefined,
    backgroundDisabled: undefined,

    boxShadowHover: undefined,
    boxShadowActive: undefined,

    iconSize: undefined,

    spinnerColor: undefined,
    spinnerBorderColor: undefined,
  },
  'ds-icon-button'
);

const buttonValues: Record<
  'text' | 'other',
  Record<
    ButtonSize,
    {
      iconSpacing: number;
      iconSize: number;
      typography: 'P10M' | 'P12M' | 'P14M' | 'P12B' | 'P14B';
      padding: string;
      height?: string;
    }
  >
> = {
  text: {
    sm: {
      iconSize: 12,
      iconSpacing: 4,
      typography: 'P10M',
      padding: '0',
    },
    md: {
      iconSize: 16,
      iconSpacing: 4,
      typography: 'P12M',
      padding: '0',
    },
    lg: {
      iconSize: 20,
      iconSpacing: 4,
      typography: 'P14M',
      padding: '0',
    },
  },
  other: {
    sm: {
      iconSize: 16,
      iconSpacing: 4,
      typography: 'P12B',
      height: '28px',
      padding: '0 10px',
    },
    md: {
      iconSize: 20,
      iconSpacing: 8,
      typography: 'P14B',
      height: '32px',
      padding: '0 12px',
    },
    lg: {
      iconSize: 20,
      iconSpacing: 8,
      typography: 'P14B',
      height: '40px',
      padding: '0 16px',
    },
  },
};

export const buttonsTheme: ThemeOptions['components'] = {
  DsButton: {
    defaultProps: {
      variant: 'primary',
      size: 'md',
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme, variant, size = 'md', fullWidth }) => {
        const styling =
          buttonValues[variant === 'text' ? 'text' : 'other'][size];
        return {
          borderRadius: tokens.theme.radius.sm,
          background: buttonVars.refs.background,
          color: buttonVars.refs.color,
          '&:hover': {
            background: buttonVars.refs.backgroundHover,
            color: buttonVars.refs.colorHover,
            boxShadow: buttonVars.refs.boxShadowHover,
          },
          '&:active': {
            background: buttonVars.refs.backgroundActive,
            color: buttonVars.refs.colorActive,
            boxShadow: buttonVars.refs.boxShadowActive,
          },
          ...buttonVars.setValues({
            iconSize: `${styling.iconSize}px`,
            iconGap: `${styling.iconSpacing}px`,
          }),
          ...theme.typography[styling.typography],
          padding: styling.padding,
          height: styling.height,
          width: fullWidth ? '100%' : 'max-content',
          '&:focus-visible': focusProps(),
          whiteSpace: 'nowrap',
        };
      },
      startIcon: {
        '& .DsButton-StartIcon': {
          display: 'inherit',
          marginRight: buttonVars.refs.iconGap,
          '& svg': {
            fontSize: buttonVars.refs.iconSize,
          },
        },
      },
      endIcon: {
        '& .DsButton-EndIcon': {
          display: 'inherit',
          marginLeft: buttonVars.refs.iconGap,
          '& svg': {
            fontSize: buttonVars.refs.iconSize,
          },
        },
      },
      primary: buttonVars.setValues({
        color: tokens.theme.fg.action.onPrimary,
        colorHover: tokens.theme.fg.action.onPrimary,
        colorActive: tokens.theme.fg.action.onPrimary,
        colorDisabled: tokens.theme.disabled.fg,
        background: tokens.theme.bg.action.primary,
        backgroundHover: tokens.theme.bg.action.primaryHover,
        backgroundActive: tokens.theme.bg.action.primaryActive,
        backgroundDisabled: tokens.theme.disabled.bg,
      }),
      secondary: buttonVars.setValues({
        color: tokens.theme.fg.action.onSecondary,
        colorHover: tokens.theme.fg.action.onSecondary,
        colorActive: tokens.theme.fg.action.onSecondary,
        colorDisabled: tokens.theme.disabled.fg,
        background: tokens.theme.bg.action.secondary,
        backgroundHover: tokens.theme.bg.action.secondaryHover,
        backgroundActive: tokens.theme.bg.action.secondaryActive,
        backgroundDisabled: tokens.theme.disabled.bg,
        boxShadowActive: boxShadowBorder(
          tokens.theme.border.action.secondaryActive
        ),
      }),
      ghost: buttonVars.setValues({
        color: tokens.theme.fg.action.onGhost,
        colorHover: tokens.theme.fg.action.onGhost,
        colorActive: tokens.theme.fg.action.onGhost,
        colorDisabled: tokens.theme.disabled.fg,
        background: undefined,
        backgroundHover: tokens.theme.bg.action.ghostHover,
        backgroundActive: tokens.theme.bg.action.ghostActive,
        boxShadowHover: boxShadowBorder(tokens.theme.border.action.ghostHover),
        boxShadowActive: boxShadowBorder(
          tokens.theme.border.action.ghostActive
        ),
        backgroundDisabled: undefined,
      }),
      danger: buttonVars.setValues({
        color: tokens.theme.fg.action.onDanger,
        colorHover: tokens.theme.fg.action.onDanger,
        colorActive: tokens.theme.fg.action.onDanger,
        colorDisabled: tokens.theme.disabled.fg,
        background: tokens.theme.bg.action.danger,
        backgroundHover: tokens.theme.bg.action.dangerHover,
        backgroundActive: tokens.theme.bg.action.dangerActive,
        backgroundDisabled: tokens.theme.disabled.bg,
      }),
      text: buttonVars.setValues({
        color: tokens.theme.fg.action.primary,
        colorHover: tokens.theme.fg.action.primaryHover,
        colorActive: tokens.theme.fg.action.primaryActive,
        colorDisabled: tokens.theme.disabled.fg,
        background: undefined,
        backgroundHover: undefined,
        backgroundActive: undefined,
        backgroundDisabled: undefined,
      }),
      disabled: {
        '&.Mui-disabled': {
          background: buttonVars.refs.backgroundDisabled,
          color: buttonVars.refs.colorDisabled,
        },
      },
      loading: {
        '& .MuiLinearProgress-root': {
          height: '2px',
          position: 'absolute',
          left: '4px',
          right: '4px',
          bottom: '2px',
          borderRadius: '3px',
          backgroundColor: 'transparent',
          opacity: 0.6,

          '& .MuiLinearProgress-bar1Indeterminate': {
            visibility: 'hidden',
          },
          '& .MuiLinearProgress-bar2Indeterminate': {
            display: 'block',
            background: tokens.theme.disabled.fg,
          },
        },
      },
    },
  },
  DsIconButton: {
    defaultProps: {
      variant: 'primary',
      size: 'md',
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.theme.radius.sm,
        background: iconButtonVars.refs.background,
        color: iconButtonVars.refs.color,

        '&:hover': {
          background: iconButtonVars.refs.backgroundHover,
          color: iconButtonVars.refs.colorHover,
          boxShadow: iconButtonVars.refs.boxShadowHover,
        },
        '&:active': {
          background: iconButtonVars.refs.backgroundActive,
          color: iconButtonVars.refs.colorActive,
          boxShadow: iconButtonVars.refs.boxShadowActive,
        },
        '&:focus-visible': focusProps(),
        '& svg': {
          fontSize: iconButtonVars.refs.iconSize,
        },
      },
      primary: iconButtonVars.setValues({
        color: tokens.theme.fg.action.onPrimary,
        colorHover: tokens.theme.fg.action.onPrimary,
        colorActive: tokens.theme.fg.action.onPrimary,
        colorDisabled: tokens.theme.disabled.fg,
        background: tokens.theme.bg.action.primary,
        backgroundHover: tokens.theme.bg.action.primaryHover,
        backgroundActive: tokens.theme.bg.action.primaryActive,
        backgroundDisabled: tokens.theme.disabled.bg,
        spinnerColor: tokens.theme.disabled.fg,
        spinnerBorderColor: 'transparent',
      }),
      secondary: iconButtonVars.setValues({
        color: tokens.theme.fg.action.onSecondary,
        colorHover: tokens.theme.fg.action.onSecondary,
        colorActive: tokens.theme.fg.action.onSecondary,
        colorDisabled: tokens.theme.disabled.fg,
        background: tokens.theme.bg.action.secondary,
        backgroundHover: tokens.theme.bg.action.secondaryHover,
        backgroundActive: tokens.theme.bg.action.secondaryActive,
        backgroundDisabled: tokens.theme.disabled.bg,
        spinnerColor: tokens.theme.disabled.fg,
        spinnerBorderColor: tokens.theme.disabled.bg,
        boxShadowActive: boxShadowBorder(
          tokens.theme.border.action.secondaryActive
        ),
      }),
      ghost: iconButtonVars.setValues({
        color: tokens.theme.fg.action.onGhost,
        colorHover: tokens.theme.fg.action.onGhost,
        colorActive: tokens.theme.fg.action.onGhost,
        colorDisabled: tokens.theme.disabled.fg,
        backgroundHover: tokens.theme.bg.action.ghostHover,
        backgroundActive: tokens.theme.bg.action.ghostActive,
        spinnerColor: tokens.theme.disabled.fg,
        spinnerBorderColor: tokens.theme.border.structural.neutralSubtle,
        boxShadowHover: boxShadowBorder(tokens.theme.border.action.ghostHover),
        boxShadowActive: boxShadowBorder(
          tokens.theme.border.action.ghostActive
        ),
      }),
      danger: iconButtonVars.setValues({
        color: tokens.theme.fg.action.onDanger,
        colorHover: tokens.theme.fg.action.onDanger,
        colorActive: tokens.theme.fg.action.onDanger,
        colorDisabled: tokens.theme.disabled.fg,
        background: tokens.theme.bg.action.danger,
        backgroundHover: tokens.theme.bg.action.dangerHover,
        backgroundActive: tokens.theme.bg.action.dangerActive,
        backgroundDisabled: tokens.theme.disabled.bg,
        spinnerColor: tokens.theme.disabled.fg,
        spinnerBorderColor: 'transparent',
      }),
      disabled: {
        '&:disabled': {
          background: iconButtonVars.getters.backgroundDisabled(),
          color: iconButtonVars.getters.colorDisabled(),
        },
      },
      lg: {
        ...iconButtonVars.setValues({ iconSize: '20px' }),
        width: '40px',
        height: '40px',
      },
      md: {
        ...iconButtonVars.setValues({ iconSize: '20px' }),
        width: '32px',
        height: '32px',
      },
      sm: {
        ...iconButtonVars.setValues({ iconSize: '16px' }),
        width: '28px',
        height: '28px',
      },
      loading: {
        '& .DsIconButton-Spinner': {
          display: 'inherit',

          '& .MuiCircularProgress-colorPrimary': {
            color: iconButtonVars.getters.spinnerColor(),
          },
          '& .DS-Spinner-withBackground:before': {
            borderColor: iconButtonVars.getters.spinnerBorderColor(),
          },
        },
      },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: () => ({
        gap: '2px',
        width: 'max-content',
        height: 'max-content',
        padding: 2,
        borderRadius: tokens.theme.radius.sm,
        backgroundColor: tokens.theme.bg.structural.surfaceRaised,
      }),
    },
  },
  MuiToggleButton: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        padding: 0,
        border: 'none',
        ...theme.typography.P14B,
        textTransform: 'initial',
        color: tokens.theme.fg.structural.secondary,
        backgroundColor: 'transparent',

        '&&&': {
          margin: 0,
          borderRadius: tokens.theme.radius.sm,

          // Beat MUI ToggleButton selected alpha(text.primary) wash (light-on-light in dark).
          '&.Mui-selected': {
            backgroundColor: tokens.theme.bg.structural.surface,
            color: tokens.theme.fg.structural.primary,
            '& .DsToggleButton-text': {
              color: tokens.theme.fg.structural.primary,
            },
            '&:hover': {
              backgroundColor: tokens.theme.bg.structural.surface,
              color: tokens.theme.fg.structural.primary,
              boxShadow: theme.shadows[DsShadow.HOVER_COMPONENT],
            },
          },
          '&:not(.Mui-selected)': {
            color: tokens.theme.fg.structural.secondary,
            '& .DsToggleButton-text': {
              color: tokens.theme.fg.structural.secondary,
            },
            '&:hover': {
              backgroundColor: tokens.theme.bg.action.surfaceSubtleHover,
            },
          },
          '&.Mui-disabled': {
            border: 'none',
            // Dark tokens: disabled.surface === disabled.fg → invisible.
            // Primary (white/grey-900) contrasts on surface; secondary was still ~2.5:1.
            '&.Mui-selected': {
              backgroundColor: tokens.theme.disabled.surface,
              color: tokens.theme.fg.structural.primary,
              '& .DsToggleButton-text': {
                color: tokens.theme.fg.structural.primary,
              },
            },
            backgroundColor: tokens.theme.disabled.bg,
            color: tokens.theme.disabled.fg,
            '& .DsToggleButton-text': {
              color: tokens.theme.disabled.fg,
            },
          },
        },

        '&.Mui-focusVisible': {
          ':after': {
            content: '""',
            position: 'absolute',
            top: -2,
            right: -2,
            bottom: -2,
            left: -2,
            borderRadius: tokens.theme.radius.sm,
            ...focusProps(),
          },
        },

        '&.MuiToggleButton-size': {
          '&Lg': {
            height: 36,
            gap: '6px',
          },
          '&Md': {
            height: 28,
            gap: '6px',
          },
          '&Sm': {
            height: 24,
            gap: '4px',
            fontSize: 12,
          },
        },
        '&.DsToggleButton-type': {
          '&Icon': {
            '&.MuiToggleButton-size': {
              '&Lg': {
                width: 36,
              },
              '&Md': {
                width: 28,
              },
              '&Sm': {
                width: 24,
              },
            },
          },
          '&Mixed': {
            '&.MuiToggleButton-size': {
              '&Lg': {
                paddingLeft: 14,
                paddingRight: 16,
              },
              '&Md': {
                paddingLeft: 10,
                paddingRight: 12,
              },
              '&Sm': {
                paddingLeft: 8,
                paddingRight: 10,
              },
            },
          },
          '&Text': {
            '&.MuiToggleButton-size': {
              '&Lg': {
                paddingLeft: 16,
                paddingRight: 16,
              },
              '&Md': {
                paddingLeft: 12,
                paddingRight: 12,
              },
              '&Sm': {
                paddingLeft: 10,
                paddingRight: 10,
              },
            },
          },
        },
      }),
    },
  },
};
